import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import taskDrawerService from './taskDrawer.service';
import {
  getShowFeedbackDrawer,
  resetTaskCount,
  incrementTaskCount,
  closeFeedbackDrawer,
  changeFeedbackDrawer,
  getIsMaxTaskCountReached,
} from '../../../../redux/slices/tasksSlice';

/**
 * Custom hook for TaskDrawer action handlers
 * Handles all task status modification actions
 */
export const useTaskDrawerActions = ({
  taskDetails,
  isDrawerOpen,
  onClose,
  onTaskUpdate,
  fetchTaskDetails,
}) => {
  const dispatch = useDispatch();
  const showFeedbackDrawer = useSelector(getShowFeedbackDrawer);
  const isReachedToMaxLimit = useSelector(getIsMaxTaskCountReached);

  /**
   * Handle cancelling a task (set status to 'Cancelled')
   */
  const handleCancelTask = useCallback(async () => {
    if (!taskDetails?.id) {
      console.error('No task ID available');
      return;
    }

    try {
      await taskDrawerService.modifyTaskStatus(
        taskDetails,
        'status_update',
        'cancelled',
        taskDetails.status // Pass the current status as oldStatus
      );

      // Notify parent component of task update
      if (onTaskUpdate) {
        onTaskUpdate();
      }
    } catch (error) {
      // Error handling is already done in the service
      console.error('Failed to cancel task:', error);
    }
  }, [taskDetails, onClose, onTaskUpdate]);

  /**
   * Handle setting task to in progress (set status to 'In Progress')
   */
  const handleInProgressTask = useCallback(async () => {
    if (!taskDetails?.id) {
      console.error('No task ID available');
      return;
    }
    try {
      await taskDrawerService.modifyTaskStatus(
        taskDetails,
        'status_update',
        'in-progress',
        taskDetails.status // Pass the current status as oldStatus
      );

      // Notify parent component of task update
      if (onTaskUpdate) {
        onTaskUpdate();
      }
    } catch (error) {
      // Error handling is already done in the service
      console.error('Failed to update task status:', error);
    }
  }, [taskDetails, onClose, onTaskUpdate]);

  /**
   * Handle completing a task (set status to 'Completed')
   */
  const handleCompleteTask = useCallback(
    async (feedbackSentiment = null, comments = null) => {
      if (!taskDetails?.id) {
        console.error('No task ID available');
        return;
      }

      try {
        await taskDrawerService.modifyTaskStatus(
          { ...taskDetails, feedbackSentiment, comments },
          'modify',
          'completed',
          taskDetails.status // Pass the current status as oldStatus
        );

        // if popup is showing so reset the count else increase the count
        if (showFeedbackDrawer) {
          dispatch(resetTaskCount());
        } else {
          dispatch(incrementTaskCount());
        }

        // Notify parent component of task update
        if (onTaskUpdate) {
          onTaskUpdate();
        }

        // Close drawer after successful action
        if (onClose) {
          onClose();
        }
      } catch (error) {
        // Error handling is already done in the service
        console.error('Failed to complete task:', error);
      }
    },
    [taskDetails, onClose, onTaskUpdate, showFeedbackDrawer, dispatch]
  );

  /**
   * Handle deleting a task (set status to 'Cancelled')
   * Note: Using 'Cancelled' status as there might not be a 'Deleted' status
   */
  const handleDeleteTask = useCallback(async () => {
    if (!taskDetails?.id) {
      console.error('No task ID available');
      return;
    }

    try {
      await taskDrawerService.modifyTaskStatus(
        taskDetails,
        'status_update',
        'cancelled'
      );

      // Notify parent component of task update
      if (onTaskUpdate) {
        onTaskUpdate();
      }

      // Close drawer after successful action
      if (onClose) {
        onClose();
      }
    } catch (error) {
      // Error handling is already done in the service
      console.error('Failed to delete task:', error);
    }
  }, [taskDetails, onClose, onTaskUpdate]);

  const handleOpenCompleteModal = useCallback(
    e => {
      e?.preventDefault();
      if (isReachedToMaxLimit) {
        dispatch(changeFeedbackDrawer(true));
      } else {
        handleCompleteTask();
      }
    },
    [dispatch, isReachedToMaxLimit, handleCompleteTask]
  );

  const handleCloseFeedbackDrawer = useCallback(() => {
    dispatch(closeFeedbackDrawer());
  }, [dispatch]);

  /**
   * Handle making a task ToDo (set status to 'Not yet Started')
   */
  // const handleMakeToDoTask = useCallback(async () => {
  //   if (!taskDetails?.id) {
  //     console.error('No task ID available');
  //     return;
  //   }

  //   try {
  //     await taskDrawerService.modifyTaskStatus(
  //       taskDetails,
  //       'status_update',
  //       'Not yet Started'
  //     );

  //     // Notify parent component of task update
  //     if (onTaskUpdate) {
  //       onTaskUpdate();
  //     }

  //     // Close drawer after successful action
  //     if (onClose) {
  //       onClose();
  //     }
  //   } catch (error) {
  //     // Error handling is already done in the service
  //     console.error('Failed to update task status:', error);
  //   }
  // }, [taskDetails, onClose, onTaskUpdate]);

  return {
    showFeedbackDrawer,

    handleOpenCompleteModal,
    handleCloseFeedbackDrawer,
    handleCancelTask,
    handleInProgressTask,
    handleCompleteTask,
    handleDeleteTask,
  };
};

export default useTaskDrawerActions;
